/**
 * Does this Claude API key look like it was truncated on paste?
 *
 * ARCHITECTURE.md §21 (decision D16): saltseed **accepts a key of any length**. The
 * field legitimately holds gateway and router tokens — LiteLLM, OpenRouter, a
 * corporate proxy — and some of those are genuinely short, so a minimum length
 * would lock paying customers out of a working configuration. The warning exists
 * only because the failure it guards against is silent: a truncated key is stored
 * happily, every call to the model 401s, and the sole symptom is "policing does not
 * work".
 *
 * <h2>Why this is deliberately narrow</h2>
 * The obvious heuristic — "warn unless it starts with `sk-ant-`" — is wrong. It
 * would nag every gateway user on every save, forever, about a configuration that
 * is correct. A warning that fires on correct input is one users learn to dismiss
 * without reading, which costs exactly the case it was built for.
 *
 * So this flags two things only, both high-confidence signs of truncation:
 *
 *  1. Shorter than {@link MIN_PREFIXABLE_LENGTH} — so short the server will not even
 *     store a disclosable prefix for it. Nothing usable is this short.
 *  2. Claims to be an Anthropic key and is far too short to be one. `sk-ant-` is a
 *     strong statement about the shape; a 20-character one is a broken paste.
 *
 * A 30-character gateway token gets no warning, because there is nothing wrong
 * with it.
 */

/** Mirrors `ApiKeyCipher.MIN_PREFIXABLE_LENGTH`. Below this the server stores no prefix. */
export const MIN_PREFIXABLE_LENGTH = 16

const ANTHROPIC_PREFIX = 'sk-ant-'

/** A real `sk-ant-api03-…` key runs to about a hundred characters. */
const SHORTEST_PLAUSIBLE_ANTHROPIC = 60

export interface ApiKeyConcern {
  /** Short label for the alert title. */
  readonly title: string
  /** What is probably wrong and what it will cost, in plain language. */
  readonly detail: string
}

/**
 * @returns a concern to show beside the field, or `undefined` when nothing looks wrong.
 *          Never blocks a save — the caller must treat this as advice.
 */
export function inspectApiKey(raw: string): ApiKeyConcern | undefined {
  const key = raw.trim()
  if (key.length === 0) return undefined

  if (key.length < MIN_PREFIXABLE_LENGTH) {
    return {
      title: 'That key looks truncated',
      detail:
        `Only ${String(key.length)} characters. Nothing usable is this short, so this was ` +
        'probably a partial paste. It will be saved either way, but every call to the model ' +
        'will fail — and it fails silently, so the only symptom is that policing never acts.',
    }
  }

  if (key.startsWith(ANTHROPIC_PREFIX) && key.length < SHORTEST_PLAUSIBLE_ANTHROPIC) {
    return {
      title: 'That looks like an incomplete Anthropic key',
      detail:
        `It starts with ${ANTHROPIC_PREFIX} but is only ${String(key.length)} characters; a real ` +
        'Anthropic key runs to about a hundred. Check you copied the whole value. It will be ' +
        'saved either way.',
    }
  }

  return undefined
}

/**
 * The same judgement for a key already stored, which the console can never see.
 *
 * It does not need to: the server withholds the prefix entirely below
 * {@link MIN_PREFIXABLE_LENGTH} (a hint that can be the whole secret is not a hint),
 * so "a key is set but there is no prefix" *is* the signal that the stored value is
 * too short — without the plaintext ever leaving the server.
 */
export function storedKeyLooksTruncated(
  hasApiKey: boolean,
  apiKeyPrefix: string | null,
): boolean {
  return hasApiKey && (apiKeyPrefix === null || apiKeyPrefix.length === 0)
}
